import { Application } from '@tencent/alloy-renderer';
import { ISlideModel } from '@src/slide/domain/ui-model/element-modeles/Interface/slide-model.interface';
import { InheritElementsLayer } from '@src/slide/content-viewer/service/canvas-renderer/core/canvas-render/slide/master-layout-elements';
import { RenderPostionType } from '@src/js/constant';
import imageCache from '@src/slide/content-viewer/service/canvas-renderer/core/canvas-graphic/fill-style/image-cache';
import { IPcEditorAppServices } from '@src/slide/application/entry/toc/editor/pc/pc-editor-app-services.interface';
import { AppServiceProvider } from '@src/slide/application/app-service-provider';
import { IRenderProps } from '@src/slide/content-viewer/service/canvas-renderer/core/canvas-entry/edit-pc/stable-layer/stable-layer.interface';

/**
 * 母版板式实例
 * 一个母版实例对应一个canvas节点
 */
export abstract class MasterLayoutService {
    protected model: ISlideModel;
    protected component: InheritElementsLayer;
    private scale: number;
    private getApplication: () => Application;

    protected abstract get elementType(): string;
    private handleStableLayerUpdate: (props: Partial<IRenderProps>) => void;

    public constructor(
        scale = 1,
        model: ISlideModel,
        getApplication: () => Application,
        handleStableLayerUpdate: (props: Partial<IRenderProps>) => void,
    ) {
        this.model = model;
        this.getApplication = getApplication;
        this.handleStableLayerUpdate = handleStableLayerUpdate;
        this.buildElement();
        this.addObserver?.();
    }

    /**
     * 获取这个母版的component
     */
    public getComponent() {
        return this.component;
    }

    public updateWithData(scale: number): void {
        this.reloadComponent(this.model);
        this.scale = scale;
    }

    /**
     * 重新加载component
     */
    public reloadComponent(model = this.model): void {
        this.component?.updateSlideModel(model, this.elementType);
    }

    // 确保渲染完成
    public async ensureRenderComplete(waitForRender = false): Promise<void> {
        // 确保图片资源加载完成
        const imgLoadComplete = imageCache.promiseModelTreeImgLoaded({ children: this.component.getElements() });
        await imgLoadComplete;
        // 强制同步渲染一次
        waitForRender && this.getApplication?.().render();
    }

    private buildElement() {
        this.component = new InheritElementsLayer(
            this.elementType,
            this.model,
            () => this.scale,
            this.getApplication,
            RenderPostionType.CANVAS,
            0,
            {
                infrastructureService: this.getAppServices().getInfrastructureService(),
                uiDataService: this.getAppServices().getUiDataService(),
                mediaManager: this.getAppServices().viewableServiceLayer.mediaManager,
                linkManager: this.getAppServices().viewableServiceLayer.linkManager,
                editingStateManager: this.getAppServices().viewableServiceLayer.editingStateManager,
                formatBrushManager: this.getAppServices().editableServiceLayer?.formatBrushManager,
                fontManager: this.getAppServices().viewableServiceLayer.fontManager,
                gifService: this.getAppServices().viewableServiceLayer.gifService,
                selectionService: this.getAppServices().viewableServiceLayer.selectionService,
                masterLayoutEditService: this.getAppServices().editableServiceLayer?.masterLayoutEditService,
            },
            true,
            false,
            this.handleStableLayerUpdate,
        );
    }

    private addObserver(): void {
        this.getAppServices().getUiDataService().presentation.convertMasterAndLayoutPromise.then(() => {
            this.model?.changeSetEngine.addChangesetObserver(() => this.reloadComponent());
        });
    }

    private getAppServices(): IPcEditorAppServices {
        return AppServiceProvider.getInstance().getAppServices() as
            IPcEditorAppServices;
    }
}
